import { useEffect, useState } from "react";
import { Building2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { COMPANY_KEY, errorMessage, get, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Invites = ({ invites, answer, busy }) => invites.map((c) => (
  <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid={`company-invite-${c.id}`}>
    <p className="text-sm text-blue-900"><span className="font-semibold">{c.name}</span> has invited you to join their team.</p>
    <div className="flex gap-2">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => answer(c, "decline")} data-testid={`company-invite-decline-${c.id}`}><X className="mr-1 h-4 w-4" /> Decline</Button>
      <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={() => answer(c, "accept")} data-testid={`company-invite-accept-${c.id}`}><Check className="mr-1 h-4 w-4" /> Accept</Button>
    </div>
  </div>
));

export const CompanyBar = ({ onChanged }) => {
  const { profile, refreshProfile } = useAuth();
  const { data, reload } = useAsync(() => get("/me/companies"), []);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (profile.company_id && localStorage.getItem(COMPANY_KEY) !== profile.company_id) localStorage.setItem(COMPANY_KEY, profile.company_id);
  }, [profile.company_id]);

  const choose = async (id) => {
    localStorage.setItem(COMPANY_KEY, id);
    await refreshProfile();
    onChanged();
  };
  const answer = async (c, action) => {
    setBusy(true);
    try {
      await post(`/me/invites/${c.id}/${action}`);
      toast.success(action === "accept" ? `You've joined ${c.name}` : "Invite declined");
      await reload();
      if (action === "accept") await choose(c.id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="company-bar">
      <Invites invites={data.invites} answer={answer} busy={busy} />
      {data.companies.length > 1 && (
        <Select value={profile.company_id || ""} onValueChange={choose}>
          <SelectTrigger className="h-12 bg-white text-base" data-testid="company-switcher" aria-label="Choose company">
            <Building2 className="mr-2 h-4 w-4 text-blue-700" /><SelectValue placeholder="Choose company" />
          </SelectTrigger>
          <SelectContent>{data.companies.map((c) => <SelectItem key={c.id} value={c.id} className="py-3" data-testid={`company-option-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {data.companies.length === 1 && <p className="flex items-center gap-2 text-sm font-medium text-slate-600" data-testid="company-name"><Building2 className="h-4 w-4 text-blue-700" /> {data.companies[0].name}</p>}
      {!data.companies.length && !data.invites.length && <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="no-companies">You&apos;re not part of a company yet. Ask a manager to invite you.</p>}
      {profile.company && !profile.company.active && <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="company-paused">This company&apos;s account is paused. Contact your manager.</p>}
    </div>
  );
};
